<script setup lang="ts">
	import { useAuthStore } from '@/stores/auth'
	import { HeartIcon as HeartOutlineIcon } from '@heroicons/vue/24/outline'
	import { HeartIcon as HeartSolidIcon } from '@heroicons/vue/24/solid'
	import moment from 'moment'
	import { Ref, computed, ref } from 'vue'
	import { Carousel, Navigation, Slide } from 'vue3-carousel'
	import { useToast } from 'vue-toastification'

	import useDateFormat from '@/composables/useDateFormat'
	import { useMarkable } from '@/composables/useMarkable'

	import NoHotelImages from '@/modules/hotels/components/NoHotelImages.vue'
	import StarsRating from '@/modules/hotels/components/StarsRating.vue'
	import { displayedCategory } from '@/modules/hotels/composables/useHotelCategory'
	import { CarouselModels } from '@/modules/hotels/interfaces/carouselModels'
	import { Hotel, Item } from '@/modules/hotels/interfaces/hotelDetailsResponse'
	import { HotelImages } from '@/modules/hotels/interfaces/hotelImages'

	import { formatLongDate } from '../utils/hotelDates'

	const userStore = useAuthStore()

	const toast = useToast()
	const { removeFavoriteHotelAsync, setFavoriteHotelAsync } = useMarkable()
	interface Props {
		hotel: Hotel
		hotelImages: HotelImages
		haveAttractions: boolean
		carouselModels: CarouselModels
		message: URL
	}

	interface Emits {
		(event: 'openMap'): () => void
		(event: 'onCopy'): () => void
		(event: 'showMultiple', i: number, images: Item[]): void
	}

	const props = defineProps<Props>()
	defineEmits<Emits>()
	const hotelDetails = ref(null)

	/**
	 * Etiqueta del tipo de alojamiento SOLO cuando no es un hotel de habitación
	 * privada (Hostal, Apartamento, Aparthotel, Camping…). Viene del Content API del
	 * proveedor; en Amadeus no existe y por eso no se pinta nada.
	 */
	/** En Hotelbeds, la categoría oficial del proveedor; si no, el rating del catálogo. */
	const category = computed(() => displayedCategory(props.hotel))

	const accommodationBadge = computed(() => {
		const raw = (props.hotel as any)?.accommodationTypeName
		if (!raw) return null
		const label = String(raw).trim()
		return /^hotel$/i.test(label) ? null : label
	})

	interface Expose {
		hotelDetails: Ref
	}

	defineExpose<Expose>({
		hotelDetails,
	})

	const isFavorite = ref(props.hotel.favorite)

	const toggleFavorite = async (hotel: Hotel) => {
		isFavorite.value = !isFavorite.value

		if (!isFavorite.value) {
			toast('Hotel eliminado de favorito')
			await removeFavoriteHotelAsync(String(hotel.id))
		} else {
			toast('Hotel agregado a favoritos')
			await setFavoriteHotelAsync(String(hotel.id))
		}
	}
</script>

<template>
	<div
		class="w-full rounded-xl md:rounded-2xl border border-gray-200 bg-white shadow-md md:shadow-xl overflow-hidden"
	>
		<div
			ref="hotelDetails"
			class="flex flex-col"
		>
			<!-- Details-->
			<div class="flex w-full flex-col">
				<!-- Hotel data -->
				<div class="bg-white p-3 sm:p-4 md:p-5">
					<!-- Details -->
					<div class="space-y-2 sm:space-y-3">
						<!-- Título, Estrellas y Botones -->
						<div
							class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 sm:gap-3"
						>
							<div class="flex-1">
								<div
									class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1.5 sm:mb-2"
								>
									<div class="min-w-0">
										<h1
											class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 capitalize leading-tight"
										>
											{{ hotel.name.toUpperCase() }}
										</h1>
										<!-- Tipo de alojamiento OFICIAL del proveedor (Hostal, Aparthotel,
										Apartamento…). Se muestra sólo cuando no es "Hotel": el
										viajero corporativo espera habitación privada. -->
										<span
											v-if="accommodationBadge"
											class="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200"
										>
											<span class="material-symbols-outlined text-[14px]"
												>info</span
											>
											{{ accommodationBadge }}
										</span>
									</div>

									<!-- Botones de acción -->
									<div class="flex items-center gap-1.5 sm:gap-2">
										<!-- Ver Mapa -->
										<button
											v-tooltip="'Ver mapa'"
											class="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 min-w-[36px] min-h-[36px] rounded-lg border border-gray-300 bg-white text-gray-700 transition-all duration-300 hover:border-[#021F58]/30 hover:bg-[#021F58]/5 hover:text-[#021F58] hover:shadow-md active:scale-95"
											@click="$emit('openMap')"
										>
											<span class="material-symbols-outlined text-lg sm:text-xl"
												>map</span
											>
										</button>

										<!-- Compartir -->
										<button
											v-tooltip="'Compartir'"
											v-clipboard:copy="message"
											v-clipboard:success="() => $emit('onCopy')"
											class="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 min-w-[36px] min-h-[36px] rounded-lg border border-gray-300 bg-white text-gray-700 transition-all duration-300 hover:border-[#021F58]/30 hover:bg-[#021F58]/5 hover:text-[#021F58] hover:shadow-md active:scale-95"
										>
											<span class="material-symbols-outlined text-lg sm:text-xl"
												>share</span
											>
										</button>

										<!-- Favoritos -->
										<button
											v-if="!userStore.user?.internal"
											v-tooltip="
												!isFavorite ? 'Añadir a favoritos' : (
													'Eliminar de favoritos'
												)
											"
											@click.stop="toggleFavorite(hotel)"
											:class="[
												'inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 min-w-[36px] min-h-[36px] rounded-lg border transition-all duration-300 hover:shadow-md active:scale-95',
												isFavorite ?
													'border-red-300 bg-red-50 text-red-500 hover:bg-red-100'
												:	'border-gray-300 bg-white text-gray-700 hover:border-red-400 hover:bg-red-50 hover:text-red-500',
											]"
										>
											<HeartOutlineIcon
												v-if="!isFavorite"
												class="h-4 w-4 sm:h-5 sm:w-5"
											/>
											<HeartSolidIcon
												v-else
												class="h-4 w-4 sm:h-5 sm:w-5 animate-heart"
											/>
										</button>
									</div>
								</div>

								<!-- Rating del catálogo, o la categoría oficial si es Hotelbeds -->
								<span
									v-if="category.text"
									v-tooltip="category.label"
									class="inline-flex w-fit items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-100"
								>
									{{ category.text }}
								</span>
								<StarsRating
									v-else
									:rating="category.stars"
									:have-attractions="haveAttractions"
								/>

								<!-- Ubicación -->
								<div
									class="flex flex-col sm:flex-row justify-between gap-2 sm:gap-3"
								>
									<div
										v-if="hotel.address"
										class="flex items-center gap-1.5"
									>
										<span
											class="material-symbols-outlined text-gray-500 text-base mt-0.5 flex-shrink-0"
											>location_on</span
										>
										<p class="text-xs sm:text-sm text-gray-600 leading-relaxed">
											{{ hotel.address.addressLine }},
											{{ hotel.address.cityName }}
											{{ hotel.address.postalCode }},
											{{ hotel.address.countryCode }}
										</p>
									</div>
									<div
										class="bg-white/70 backdrop-blur-sm rounded-lg border border-gray-200 p-3"
									>
										<div
											class="flex flex-wrap items-center justify-center gap-3"
										>
											<div class="flex items-center gap-2">
												<span
													class="material-symbols-outlined text-[#021F58] text-lg"
													>calendar_today</span
												>
												<div>
													<p class="text-xs text-gray-500">Check-in</p>
													<p class="text-sm font-semibold text-gray-900">
														{{ formatLongDate(String($route.query.Start)) }}
													</p>
												</div>
											</div>

											<span class="material-symbols-outlined text-gray-400"
												>arrow_forward</span
											>

											<div class="flex items-center gap-1.5">
												<span
													class="material-symbols-outlined text-[#021F58] text-base"
													>event</span
												>
												<div>
													<p class="text-[10px] text-gray-500">Check-out</p>
													<p class="text-xs font-semibold text-gray-900">
														{{ formatLongDate(String($route.query.End)) }}
													</p>
												</div>
											</div>

											<div class="h-6 w-px bg-gray-300"></div>

											<div class="flex items-center gap-1.5">
												<span
													class="material-symbols-outlined text-[#021F58] text-base"
													>nights_stay</span
												>
												<p class="text-xs font-semibold text-gray-900">
													{{
														moment(String($route.query.End)).diff(
															String($route.query.Start),
															'days'
														)
													}}
													{{
														(
															moment(String($route.query.End)).diff(
																String($route.query.Start),
																'days'
															) === 1
														) ?
															'noche'
														:	'noches'
													}}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Images - Premium Gallery -->
			<div
				:class="[
					'relative flex overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black h-[14rem] sm:h-[16rem] md:h-[20rem] lg:h-[24rem]',
					!(hotelImages.roomImages.length > 0 && carouselModels[6]) && '!h-fit',
				]"
			>
				<div
					:class="[
						'w-full md:w-3/5 relative',
						hotelImages.randomRoomImages.length === 0 && '!w-full',
					]"
				>
					<!-- Room images -->
					<Carousel
						v-if="hotelImages.roomImages.length > 0 && carouselModels[6]"
						id="gallery"
						:v-model="carouselModels[6].value"
						:items-to-show="1"
						:transition="500"
					>
						<Slide
							v-for="(image, a) in hotelImages.roomImages"
							:key="image.url"
							class="cursor-pointer relative group/slide"
						>
							<div
								class="carousel__item !rounded-none relative overflow-hidden shadow-2xl"
								@click="$emit('showMultiple', a, hotelImages.roomImages)"
							>
								<img
									class="aspect-[6/5] sm:aspect-[6/4] h-full w-full object-cover md:aspect-[16/9] transform transition-all duration-700 group-hover/slide:scale-110"
									:src="image.url"
									alt=""
								/>
								<!-- Overlay suave -->
								<div
									class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/slide:opacity-100 transition-all duration-500"
								></div>
								<!-- Indicador de click -->
								<div
									class="absolute inset-0 flex items-center justify-center opacity-0 group-hover/slide:opacity-100 transition-all duration-500"
								>
									<div
										class="relative bg-white/90 backdrop-blur-xl rounded-full p-2.5 sm:p-3 shadow-md border-2 border-white/80"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
											class="w-5 h-5 sm:w-6 sm:h-6 text-[#021F58]"
										>
											<path
												d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
											></path>
											<circle
												cx="12"
												cy="12"
												r="3"
											></circle>
										</svg>
										<div class="hidden"></div>
									</div>
								</div>
							</div>
						</Slide>
						<template #addons>
							<Navigation />
						</template>
					</Carousel>

					<NoHotelImages v-else />
				</div>

				<!-- Thumbnails Grid - Clickeable -->
				<div
					v-if="hotelImages.randomRoomImages.length > 0"
					class="hidden flex-1 flex-row md:flex md:w-2/5 md:flex-col gap-1.5 sm:gap-2 p-1 sm:p-1.5"
				>
					<div
						v-for="(image, idx) in hotelImages.randomRoomImages"
						:key="image.url"
						class="flex-1 relative group/thumb cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-500"
						@click="$emit('showMultiple', idx, hotelImages.randomRoomImages)"
					>
						<img
							class="h-full w-full object-cover transform transition-all duration-700 group-hover/thumb:scale-110"
							:src="image.url"
							:alt="image.description"
						/>
						<!-- Overlay suave -->
						<div
							class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/thumb:opacity-100 transition-all duration-500"
						></div>
						<!-- Indicador de zoom -->
						<div
							class="absolute inset-0 flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-all duration-500"
						>
							<div
								class="relative bg-white/90 backdrop-blur-xl rounded-full p-2 sm:p-2.5 shadow-md border-2 border-white/80"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
									class="w-4 h-4 sm:w-5 sm:h-5 text-[#021F58]"
								>
									<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
									<circle
										cx="12"
										cy="12"
										r="3"
									></circle>
								</svg>
								<div class="hidden"></div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
	@keyframes hoverpulse {
		100% {
			transform: scale(1);
		}

		50% {
			transform: scale(0.8);
		}

		0% {
			transform: scale(1);
		}
	}

	@keyframes pulse {
		0% {
			transform: scale(1);
		}

		50% {
			transform: scale(1.2);
		}

		100% {
			transform: scale(1);
		}
	}

	.animate-heart {
		animation: pulse 0.5s ease-in-out;
	}

	.animate-heart-hover {
		animation: hoverpulse 0.5s ease-out;
	}

	/* Estilos para botones de navegación del carrusel */
	:deep(.carousel__prev),
	:deep(.carousel__next) {
		/* Hacer los botones circulares */
		width: 20px !important;
		height: 20px !important;
		border-radius: 50% !important;

		/* Estilos visuales */
		background: linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.95),
			rgba(255, 255, 255, 0.85)
		) !important;

		/* Siempre visibles */
		opacity: 1 !important;

		/* Color del icono */
		color: #021f58 !important;

		/* Transiciones */
		transition: all 0.3s ease !important;

		/* Centrar contenido */
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
	}

	/* Tamaños responsivos */
	@media (min-width: 640px) {
		:deep(.carousel__prev),
		:deep(.carousel__next) {
			width: 48px !important;
			height: 48px !important;
		}
	}
</style>
